// context/TrainerOnboardingContext.js
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from '../axiosConfig';
import { TrainerContext } from './TrainerContext';
import { useSubscription } from './SubscriptionContext';
import i18n from '../i18n';

const TrainerOnboardingContext = createContext();

// Local storage key for persisting tour state during a session
const TRAINER_ONBOARDING_STATE_KEY = 'trainer_onboarding_state';

/**
 * Trainer onboarding steps:
 * 0 - Welcome modal (asks if user wants to proceed)
 * 1 - Podopieczni (Pets/Dogs)
 * 2 - Opiekunowie (Owners)
 * 3 - Ćwiczenia (Exercises)
 * 4 - Raporty (Reports)
 * 5 - Ankiety (Surveys)
 * 6 - Kalendarz (Calendar)
 * 7 - Treningi (Trainings)
 * 8 - Grupy (Groups)
 * 9 - Szkoła (School)
 * 10 - Moje konto (My Account)
 */
export const TRAINER_ONBOARDING_STEPS = {
    WELCOME: 0,
    PETS: 1,
    OWNERS: 2,
    EXERCISES: 3,
    REPORTS: 4,
    SURVEYS: 5,
    CALENDAR: 6,
    TRAININGS: 7,
    GROUPS: 8,
    SCHOOL: 9,
    MY_ACCOUNT: 10,
};

export const TRAINER_ONBOARDING_STEP_CONFIG = [
    {
        id: 'welcome',
        titleKey: 'onboarding:trainer.welcome.title',
        descriptionKey: 'onboarding:trainer.welcome.description',
        targetSelector: null, // No target for welcome modal
    },
    {
        id: 'pets',
        titleKey: 'onboarding:trainer.pets.title',
        descriptionKey: 'onboarding:trainer.pets.description',
        targetSelector: '[data-onboarding="pets"]',
        route: '/podopieczni',
    },
    {
        id: 'owners',
        titleKey: 'onboarding:trainer.owners.title',
        descriptionKey: 'onboarding:trainer.owners.description',
        targetSelector: '[data-onboarding="owners"]',
        route: '/owners/all',
    },
    {
        id: 'exercises',
        titleKey: 'onboarding:trainer.exercises.title',
        descriptionKey: 'onboarding:trainer.exercises.description',
        targetSelector: '[data-onboarding="exercises"]',
        route: '/exercises',
    },
    {
        id: 'reports',
        titleKey: 'onboarding:trainer.reports.title',
        descriptionKey: 'onboarding:trainer.reports.description',
        targetSelector: '[data-onboarding="reports"]',
        route: '/reports',
    },
    {
        id: 'surveys',
        titleKey: 'onboarding:trainer.surveys.title',
        descriptionKey: 'onboarding:trainer.surveys.description',
        targetSelector: '[data-onboarding="surveys"]',
        route: '/survey',
    },
    {
        id: 'calendar',
        titleKey: 'onboarding:trainer.calendar.title',
        descriptionKey: 'onboarding:trainer.calendar.description',
        targetSelector: '[data-onboarding="calendar"]',
        route: '/calendar',
    },
    {
        id: 'trainings',
        titleKey: 'onboarding:trainer.trainings.title',
        descriptionKey: 'onboarding:trainer.trainings.description',
        targetSelector: '[data-onboarding="trainings"]',
        route: '/trainings/confirmed',
    },
    {
        id: 'groups',
        titleKey: 'onboarding:trainer.groups.title',
        descriptionKey: 'onboarding:trainer.groups.description',
        targetSelector: '[data-onboarding="groups"]',
        route: '/trainer/groups',
    },
    {
        id: 'school',
        titleKey: 'onboarding:trainer.school.title',
        descriptionKey: 'onboarding:trainer.school.description',
        targetSelector: '[data-onboarding="school"]',
        route: '/school/details',
    },
    {
        id: 'my-account',
        titleKey: 'onboarding:trainer.account.title',
        descriptionKey: 'onboarding:trainer.account.description',
        targetSelector: '[data-onboarding="my-account"]',
        route: '/trainer/account',
    },
];

export const TrainerOnboardingProvider = ({ children }) => {
    // Get trainer data from TrainerContext
    const trainerContext = useContext(TrainerContext);
    const trainer = trainerContext?.trainer;
    const trainerLoading = trainerContext?.loading;
    const refreshTrainerData = trainerContext?.refreshTrainerData;

    // Get subscription status - don't show onboarding if subscription modal is blocking
    const subscriptionContext = useSubscription();
    const isSubscriptionLocked = subscriptionContext?.isLocked?.() ?? false;
    const subscriptionLoading = subscriptionContext?.loading ?? false;

    // Helper to get saved tour state from localStorage
    const getSavedState = () => {
        try {
            const saved = localStorage.getItem(TRAINER_ONBOARDING_STATE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error('Error parsing trainer onboarding state:', e);
        }
        return null;
    };

    // Onboarding completion status (from backend)
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true); // Default to true until we know
    const [isInitialized, setIsInitialized] = useState(false);

    // Is the onboarding tour currently active (restore from localStorage)
    const [isOnboardingActive, setIsOnboardingActive] = useState(() => {
        const savedState = getSavedState();
        return savedState?.isActive || false;
    });

    // Current step in the onboarding flow (restore from localStorage)
    const [currentStep, setCurrentStep] = useState(() => {
        const savedState = getSavedState();
        return savedState?.currentStep || TRAINER_ONBOARDING_STEPS.WELCOME;
    });

    // Should show the welcome modal
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // API call state
    const [isUpdating, setIsUpdating] = useState(false);

    // Initialize from trainer data when available (only once)
    useEffect(() => {
        // Only initialize once - don't re-initialize when trainer data refreshes
        if (isInitialized) return;

        // Wait for trainer data to be loaded (loading must be explicitly false, not undefined)
        const isLoading = trainerLoading === true;
        if (!isLoading && trainer) {
            // Treat null, undefined, or false as "not completed"
            // Only true means completed
            const completed = trainer.onboardingCompleted === true;
            setHasCompletedOnboarding(completed);
            setIsInitialized(true);
        }
    }, [trainer, trainerLoading, isInitialized]);

    // Show welcome modal after initialization if onboarding not completed
    // Don't show if subscription modal is blocking access
    useEffect(() => {
        // Wait for subscription to be loaded and not blocking
        const isSubscriptionBlocking = subscriptionLoading || isSubscriptionLocked;

        if (isInitialized && !hasCompletedOnboarding && !isOnboardingActive && !showWelcomeModal && !isSubscriptionBlocking) {
            const timer = setTimeout(() => {
                setShowWelcomeModal(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isInitialized, hasCompletedOnboarding, isOnboardingActive, showWelcomeModal, subscriptionLoading, isSubscriptionLocked]);

    // Persist tour state to localStorage when it changes
    useEffect(() => {
        if (isOnboardingActive) {
            localStorage.setItem(TRAINER_ONBOARDING_STATE_KEY, JSON.stringify({
                isActive: isOnboardingActive,
                currentStep: currentStep,
            }));
        } else {
            localStorage.removeItem(TRAINER_ONBOARDING_STATE_KEY);
        }
    }, [isOnboardingActive, currentStep]);

    /**
     * Mark onboarding as completed in the backend
     */
    const markOnboardingComplete = useCallback(async () => {
        try {
            setIsUpdating(true);
            const token = localStorage.getItem('jwt');
            await axios.put('/api/v1/trainer/onboarding/complete', {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setHasCompletedOnboarding(true);
            // Refresh trainer data so the new status persists across page navigations
            if (refreshTrainerData) {
                await refreshTrainerData();
            }
        } catch (error) {
            console.error('Failed to update trainer onboarding status:', error);
            // Still mark as completed locally even if API fails
            setHasCompletedOnboarding(true);
        } finally {
            setIsUpdating(false);
        }
    }, [refreshTrainerData]);

    /**
     * Reset onboarding in the backend (for "Moje konto" page)
     */
    const resetOnboardingInBackend = useCallback(async () => {
        try {
            setIsUpdating(true);
            const token = localStorage.getItem('jwt');
            await axios.put('/api/v1/trainer/onboarding/reset', {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setHasCompletedOnboarding(false);
            setIsOnboardingActive(false);
            setCurrentStep(TRAINER_ONBOARDING_STEPS.WELCOME);
            setShowWelcomeModal(true);
            localStorage.removeItem(TRAINER_ONBOARDING_STATE_KEY);
            // Refresh trainer data
            if (refreshTrainerData) {
                await refreshTrainerData();
            }
        } catch (error) {
            console.error('Failed to reset trainer onboarding status:', error);
        } finally {
            setIsUpdating(false);
        }
    }, [refreshTrainerData]);

    /**
     * Start the onboarding tour from step 1 (after welcome modal)
     */
    const startOnboarding = useCallback(() => {
        setShowWelcomeModal(false);
        setIsOnboardingActive(true);
        setCurrentStep(TRAINER_ONBOARDING_STEPS.PETS);
    }, []);

    /**
     * Skip the onboarding and mark as completed
     */
    const skipOnboarding = useCallback(() => {
        setShowWelcomeModal(false);
        setIsOnboardingActive(false);
        markOnboardingComplete();
        localStorage.removeItem(TRAINER_ONBOARDING_STATE_KEY);
    }, [markOnboardingComplete]);

    /**
     * Complete the onboarding tour
     */
    const completeOnboarding = useCallback(() => {
        setIsOnboardingActive(false);
        setCurrentStep(TRAINER_ONBOARDING_STEPS.WELCOME);
        markOnboardingComplete();
        localStorage.removeItem(TRAINER_ONBOARDING_STATE_KEY);
    }, [markOnboardingComplete]);

    /**
     * Move to the next step
     */
    const nextStep = useCallback(() => {
        const maxStep = TRAINER_ONBOARDING_STEP_CONFIG.length - 1;
        if (currentStep < maxStep) {
            setCurrentStep(prev => prev + 1);
        } else {
            // Complete onboarding if at last step
            completeOnboarding();
        }
    }, [currentStep, completeOnboarding]);

    /**
     * Move to the previous step
     */
    const prevStep = useCallback(() => {
        if (currentStep > TRAINER_ONBOARDING_STEPS.PETS) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    /**
     * Reset onboarding state (calls backend API)
     */
    const resetOnboarding = useCallback(() => {
        resetOnboardingInBackend();
    }, [resetOnboardingInBackend]);

    /**
     * Get current step configuration
     */
    const getCurrentStepConfig = useCallback(() => {
        const config = TRAINER_ONBOARDING_STEP_CONFIG[currentStep];
        if (!config) return null;
        return {
            ...config,
            title: i18n.t(config.titleKey),
            description: i18n.t(config.descriptionKey),
        };
    }, [currentStep]);

    /**
     * Check if we're on the last step
     */
    const isLastStep = currentStep === TRAINER_ONBOARDING_STEP_CONFIG.length - 1;

    /**
     * Check if we're on the first tour step (not welcome)
     */
    const isFirstTourStep = currentStep === TRAINER_ONBOARDING_STEPS.PETS;

    const value = {
        // State
        hasCompletedOnboarding,
        isOnboardingActive,
        currentStep,
        showWelcomeModal,
        isLastStep,
        isFirstTourStep,
        isInitialized,
        isUpdating,

        // Actions
        startOnboarding,
        skipOnboarding,
        nextStep,
        prevStep,
        completeOnboarding,
        resetOnboarding,

        // Helpers
        getCurrentStepConfig,
        totalSteps: TRAINER_ONBOARDING_STEP_CONFIG.length - 1, // Exclude welcome
    };

    return (
        <TrainerOnboardingContext.Provider value={value}>
            {children}
        </TrainerOnboardingContext.Provider>
    );
};

export const useTrainerOnboarding = () => {
    const context = useContext(TrainerOnboardingContext);
    if (!context) {
        throw new Error('useTrainerOnboarding must be used within a TrainerOnboardingProvider');
    }
    return context;
};

export default TrainerOnboardingContext;
